'use client';

import { useEffect, useRef, useState } from 'react';
import styles from './cinema.module.css';
import { LOGO_BARS } from './strip';
import { useSoundPreference } from '../sound';

function readerWantsLessMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function PageBackdrop({ imageUrl }: { imageUrl: string | null }): React.ReactElement {
  const [urlThatFailedToLoad, setUrlThatFailedToLoad] = useState<string | null>(null);
  const artThatLoads = imageUrl && imageUrl !== urlThatFailedToLoad ? imageUrl : null;

  return (
    <div className={styles.backdrop ?? ''} aria-hidden="true">
      {artThatLoads ? (
        <img
          className={styles.backdropArt ?? ''}
          src={artThatLoads}
          alt=""
          onError={() => setUrlThatFailedToLoad(artThatLoads)}
        />
      ) : null}
    </div>
  );
}

export function GameTitle({
  title,
  logoUrl,
  as: Heading = 'h1',
  className = 'gb-display',
}: {
  title: string;
  logoUrl: string | null;
  as?: 'h1' | 'h2';
  className?: string;
}): React.ReactElement {
  if (logoUrl) {
    return (
      <Heading style={{ margin: 0 }}>
        <img className={styles.gameLogo ?? ''} src={logoUrl} alt={title} />
      </Heading>
    );
  }

  return (
    <Heading className={className} style={{ margin: 0, maxWidth: '16ch' }}>
      {title}
    </Heading>
  );
}

export function HeldBeat({
  afterMs,
  children,
}: {
  afterMs: number;
  children: React.ReactNode;
}): React.ReactElement | null {
  const [arrived, setArrived] = useState(() => readerWantsLessMotion());

  useEffect(() => {
    if (arrived) return;
    const timer = window.setTimeout(() => setArrived(true), afterMs);
    return () => window.clearTimeout(timer);
  }, [afterMs, arrived]);

  if (!arrived) return null;

  return <div className={styles.beat ?? ''}>{children}</div>;
}

const COUNT_UP_DEFAULT_MS = 900;

export function CountUp({
  to,
  format,
  durationMs = COUNT_UP_DEFAULT_MS,
  className,
}: {
  to: number;
  format: (value: number) => string;
  durationMs?: number;
  className?: string | undefined;
}): React.ReactElement {
  const [shown, setShown] = useState(() => (readerWantsLessMotion() ? to : 0));
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (readerWantsLessMotion()) {
      setShown(to);
      return;
    }

    startedAtRef.current = null;
    let frame = 0;

    const step = (nowMs: number): void => {
      startedAtRef.current ??= nowMs;
      const elapsed = nowMs - startedAtRef.current;
      const share = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - share, 3);

      setShown(to * eased);
      if (share < 1) frame = window.requestAnimationFrame(step);
    };

    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [to, durationMs]);

  return (
    <span className={className ?? 'gb-stat'} aria-label={format(to)}>
      <span aria-hidden="true">{format(shown)}</span>
    </span>
  );
}

export function VoltFlash({ trigger }: { trigger: string | null }): React.ReactElement | null {
  if (!trigger) return null;

  return <i key={trigger} className={styles.voltFlash ?? ''} aria-hidden="true" />;
}

export interface ReceiptRow {
  label: string;
  value: string;
}

export function Receipt({ rows }: { rows: readonly ReceiptRow[] }): React.ReactElement {
  return (
    <div className={styles.receipt ?? ''}>
      {rows.map((row, index) => (
        <ReceiptEntry key={row.label} row={row} ruledAbove={index > 0} />
      ))}
    </div>
  );
}

function ReceiptEntry({
  row,
  ruledAbove,
}: {
  row: ReceiptRow;
  ruledAbove: boolean;
}): React.ReactElement {
  return (
    <>
      {ruledAbove ? <i className={styles.receiptRule ?? ''} aria-hidden="true" /> : null}
      <span className={styles.receiptLabel ?? ''}>{row.label}</span>
      <span className={styles.receiptValue ?? ''}>{row.value}</span>
    </>
  );
}

export function NothingYet({
  headline,
  children,
}: {
  headline: string;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={styles.nothingYet ?? ''}>
      <p className={styles.nothingYetHeadline ?? ''}>{headline}</p>
      {children}
    </div>
  );
}

export function SoundToggle(): React.ReactElement {
  const { enabled, toggle } = useSoundPreference();

  return (
    <button
      type="button"
      className={styles.soundToggle ?? ''}
      data-sound={enabled ? 'on' : 'off'}
      aria-pressed={enabled}
      onClick={toggle}
      title={enabled ? 'Turn the sound off' : 'Turn the sound on'}
    >
      <span className={styles.soundToggleBars ?? ''} aria-hidden="true">
        {LOGO_BARS.map((height) => (
          <i key={height} style={{ height: `${height}%` }} />
        ))}
      </span>
      Sound {enabled ? 'on' : 'off'}
    </button>
  );
}
